import { memo, useCallback } from 'react';

import { Check } from '@air/next-icons';
import { DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { Tooltip } from '@air/primitive-tooltip';

import { type AiModelConfig } from '../../../api/aimodels/useAiModels';
import { ModelCostBadge } from './ModelCostBadge';
import { ModelTooltipContent } from './ModelTooltipContent';

type ModelItemProps = {
  disabled?: boolean;
  isSelected: boolean;
  model: AiModelConfig;
  onSelect: (model: AiModelConfig) => void;
};

export const ModelItem = memo(({ disabled, isSelected, model, onSelect }: ModelItemProps) => {
  const handleSelect = useCallback(() => {
    onSelect(model);
  }, [model, onSelect]);

  return (
    <Tooltip
      className="w-[200px] rounded-8 border border-gray-4 bg-surface-1 p-4 text-gray-12 shadow-popover"
      hideArrow
      label={model.description ? <ModelTooltipContent model={model} /> : undefined}
      side="left"
      sideOffset={4}
    >
      <DropdownMenuItem disabled={disabled} onSelect={handleSelect}>
        <div className="flex min-w-0 grow items-center gap-1.5 text-14">
          <span className="truncate">{model.name}</span>
          {(model.defaultCredits !== undefined || model.defaultDurationMs !== undefined) && (
            <ModelCostBadge credits={model.defaultCredits} durationMs={model.defaultDurationMs} />
          )}
        </div>
        {isSelected && !disabled && (
          <span className="ml-2 flex shrink-0 items-center">
            <span className="sr-only">Selected</span>
            <Check className="size-4 shrink-0 fill-blue-9" />
          </span>
        )}
      </DropdownMenuItem>
    </Tooltip>
  );
});

ModelItem.displayName = 'ModelItem';
